import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home,
  FolderKanban,
  Database,
  Cpu,
  Monitor,
  BookOpen,
  SquareChevronLeft,
  SquareChevronRight,
} from 'lucide-react';

import './Sidebar.css';
import SidebarItem from './SidebarItem';

import { ToolType, toolTypeToPath, UserType } from '../../utils/types';
import { useAppSelector } from '../../app/hooks';
import SidebarGroup from './SidebarGroup';
import SidebarSubItem from './SidebarSubItem';
import { ReactComponent as SrcnetLogo } from '../../icons/srcnet-simple.svg';

interface SidebarProps {
  selectedTool: ToolType;
  collapsed: boolean;
  onToolSelect: (tool: ToolType) => void;
  onClickHandler: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onToggleCollapse: () => void;
}

const Sidebar = ({
  selectedTool,
  collapsed,
  onToolSelect,
  onClickHandler,
  onToggleCollapse,
}: SidebarProps) => {
  const navigate = useNavigate();
  const sG_LoggedIn = useAppSelector((state) => state.global.loggedIn);
  const sG_UserType = useAppSelector((state) => state.global.userType);
  const [expandedGroup, setExpandedGroup] = useState<
    'data' | 'compute' | 'interactive' | null
  >(null);

  const handleNavigation = (tool: ToolType) => {
    onToolSelect(tool);
    navigate(toolTypeToPath[tool]);
  };

  const handleGroupToggle = (group: 'data' | 'compute' | 'interactive') => {
    if (collapsed) {
      onToggleCollapse();
      setExpandedGroup(group);
      return;
    }

    setExpandedGroup(expandedGroup === group ? null : group);
  };

  return (
    <aside className={`gateway-sidebar ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="sidebar-header">
        {!collapsed && (
          <SrcnetLogo
            style={{ marginLeft: '12px' }}
            className="text-colour"
            width={125}
            height={38}
            strokeWidth={1.5}
          />
        )}

        <button
          className="sidebar-collapse-btn"
          onClick={onToggleCollapse}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <SquareChevronRight size={30} strokeWidth={1.5} />
          ) : (
            <SquareChevronLeft size={30} strokeWidth={1.5} />
          )}
        </button>
      </div>
      {/* Navigation */}
      <div className="sidebar-navigation">
        <SidebarItem
          label="Home"
          icon={Home}
          active={selectedTool === ToolType.HOME}
          collapsed={collapsed}
          onClick={() => {
            setExpandedGroup(null);
            handleNavigation(ToolType.HOME);
          }}
        />

        <SidebarItem
          label="My Project"
          icon={FolderKanban}
          active={selectedTool === ToolType.PROJECT}
          disabled={!sG_LoggedIn}
          collapsed={collapsed}
          onClick={() => {
            setExpandedGroup(null);
            handleNavigation(ToolType.PROJECT);
          }}
        />

        <SidebarGroup
          label="Data"
          icon={Database}
          collapsed={collapsed}
          expanded={expandedGroup === 'data'}
          onToggle={() => handleGroupToggle('data')}
        >
          <SidebarSubItem
            label="Search catalogue"
            active={selectedTool === ToolType.SEARCH_CATALOG}
            onClick={() => handleNavigation(ToolType.SEARCH_CATALOG)}
          />

          <SidebarSubItem
            label="Search catalogue (CAOM)"
            active={selectedTool === ToolType.SEARCH_CATALOG_CAOM}
            onClick={() => handleNavigation(ToolType.SEARCH_CATALOG_CAOM)}
            visible={sG_UserType === UserType.DEVELOPER}
          />

          <SidebarSubItem
            label="File browser"
            disabled={!sG_LoggedIn}
            active={selectedTool === ToolType.FILE_BROWSER}
            onClick={() => handleNavigation(ToolType.FILE_BROWSER)}
            visible={[UserType.DEVELOPER, UserType.OPERATOR].includes(sG_UserType!)}
          />

          <SidebarSubItem
            label="Data-transfer jobs"
            disabled={!sG_LoggedIn}
            active={selectedTool === ToolType.DATA_TRANSFER_JOBS}
            onClick={() => handleNavigation(ToolType.DATA_TRANSFER_JOBS)}
            visible={[UserType.DEVELOPER, UserType.OPERATOR].includes(sG_UserType!)}
          />
        </SidebarGroup>

        <SidebarGroup
          label="Compute"
          icon={Cpu}
          collapsed={collapsed}
          expanded={expandedGroup === 'compute'}
          onToggle={() => handleGroupToggle('compute')}
          visible={[UserType.DEVELOPER, UserType.OPERATOR].includes(sG_UserType!)}
        >
          <SidebarSubItem
            label="Search compute resources"
            disabled={!sG_LoggedIn}
            active={selectedTool === ToolType.SEARCH_COMPUTE}
            onClick={() => handleNavigation(ToolType.SEARCH_COMPUTE)}
            visible={[UserType.DEVELOPER, UserType.OPERATOR].includes(sG_UserType!)}
          />
          <SidebarSubItem
            label="Compute jobs"
            disabled={!sG_LoggedIn}
            active={selectedTool === ToolType.COMPUTE_JOBS}
            onClick={() => handleNavigation(ToolType.COMPUTE_JOBS)}
            visible={sG_UserType === UserType.DEVELOPER}
          />
          <SidebarSubItem
            label="Search software"
            disabled={!sG_LoggedIn}
            active={selectedTool === ToolType.SEARCH_SOFTWARE}
            onClick={() => handleNavigation(ToolType.SEARCH_SOFTWARE)}
            visible={sG_UserType === UserType.DEVELOPER}
          />
        </SidebarGroup>

        <SidebarItem
          label="Interactive Sessions"
          icon={Monitor}
          active={selectedTool === ToolType.INTERACTIVE_SESSIONS}
          collapsed={collapsed}
          onClick={() => {
            setExpandedGroup(null);
            handleNavigation(ToolType.INTERACTIVE_SESSIONS);
          }}
        />
      </div>

      {/* Bottom */}
      <div className="sidebar-bottom">
        <SidebarItem
          name="user-guide"
          label="User guide"
          icon={BookOpen}
          collapsed={collapsed}
          onClick={onClickHandler}
        />
      </div>
    </aside>
  );
};

export default Sidebar;
